import Loader from '@/components/Loader';
import { useFilter } from '@/hooks/filter';
import { useTheme } from '@/hooks/theme';
import { Box, Checkbox, CheckboxGroup, Stack } from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const ChakraCheckboxFilter: React.FC<any> = ({
  onSelect,
  options,
  initialSelected,
  onPaginate,
  changePageOn,
  currentPage,
  setCurrentPage,
  hasNextPage,
  isMobile,
  maxSelection,
}) => {
  const stackRef = useRef(null);
  const { loading } = useFilter();
  const { colors } = useTheme();
  const [selected, setSelected] = useState<Option[]>(() => {
    if (initialSelected) {
      return Array.isArray(initialSelected)
        ? initialSelected.map((obj) => {
            if (typeof obj === 'string') {
              const currentOption = options.find(
                (option) => option?.value === obj,
              );
              return {
                id: obj,
                name: currentOption?.key || obj,
              };
            }
            return {
              id: obj.value,
              name: obj.label,
            };
          })
        : [{ name: initialSelected.label, id: initialSelected.value }];
    }
    return [] as Option[];
  });

  const handleSelect = useCallback((value: Option, checked: boolean) => {
    if (checked) {
      setSelected((prev) => {
        if (prev.length) return [...prev, value];
        return [value];
      });
    } else {
      setSelected((prev) => {
        const rest = prev.filter((prevObj) => {
          return prevObj.name !== value.name;
        });
        if (rest.length) {
          return rest;
        }
        return [];
      });
    }
  }, []);

  useEffect(() => {
    if (!selected.length) {
      onSelect(null);
    }
    if (selected.length) {
      onSelect(selected.map((option) => option.name));
    }
  }, [selected]);

  const optionsWithSelectState = useMemo(() => {
    return options?.map(
      ({ key: id, value: name }: { key: string; value: any }) => {
        const optionSelected = selected.find((obj) => obj.name === name);
        if (optionSelected) return { ...{ id, name }, checked: true };
        return { ...{ id, name }, checked: false };
      },
    );
  }, [options, selected]);

  if (loading && !changePageOn) {
    return (
      <Box
        display="flex"
        height="240px"
        justifyContent="center"
        alignItems="center"
      >
        <Loader loadColor={colors.primary} />
      </Box>
    );
  }

  const handleScroll = async () => {
    const stackElement: any = stackRef.current;
    if (
      stackElement?.scrollTop + stackElement?.clientHeight ===
      stackElement?.scrollHeight
    ) {
      onPaginate(currentPage + 1);
      setCurrentPage((prevPage: number) => prevPage + 1);
    }
  };
  const checkScroll = !changePageOn && hasNextPage && handleScroll;

  return (
    <CheckboxGroup defaultValue={selected.map((selected) => selected.name)}>
      <Stack
        ref={stackRef}
        spacing="0.5"
        direction="column"
        paddingX="2"
        maxHeight={isMobile ? 'auto' : '240px'}
        height={isMobile ? '100%' : '240px'}
        overflowY="scroll"
        onScroll={checkScroll ? handleScroll : undefined}
      >
        {optionsWithSelectState?.map((opt: any) => {
          const disabled = !maxSelection ? false :
            !(selected.filter((f) => f.id === opt.id).length > 0) &&
            !(selected.length < maxSelection);
          return (
            <Checkbox
              key={opt?.id}
              size="lg"
              paddingY="1"
              onChange={(e) =>
                handleSelect({ id: opt.id, name: opt.name }, e.target.checked)
              }
              value={opt.name}
              isChecked={opt.checked}
              checked={opt.checked}
              iconColor={colors.primary}
              colorScheme="whiteAlpha"
              _hover={{
                bgColor: 'gray.100',
              }}
              _focus={{
                boxShadow: `0 0 0 2 ${colors.primary}`,
              }}
              disabled={disabled}
            >
              {opt?.id}
            </Checkbox>
          );
        })}
        {changePageOn && <p style={{ margin: '0 5px' }}>Carregando...</p>}
      </Stack>
    </CheckboxGroup>
  );
};

export default ChakraCheckboxFilter;
